/* --------------------------------------------------------------------------
   src/lib/supabase/forum.ts
   Clean version – all counters maintained by DB triggers
   -------------------------------------------------------------------------- */

import { supabase } from '../supabase';
import { storage } from './storage'; // Adjust path if needed
import {
  ForumCategory,
  ForumTopic,
  ForumReply,
  ForumStats
} from '../../types/forum';

/** ─────────────────────────  Topic update  ──────────────────────────── */
export async function updateTopic(
  topicId: string,
  title: string,
  content: string
): Promise<boolean> {
  const { data: isClubTopic } = await supabase.rpc('club_forum_topic_exists', {
    topic_id_param: topicId
  });
  if (isClubTopic) {
    const { data, error } = await supabase
      .from('club_forum_topics')
      .update({ title, content, updated_at: new Date().toISOString() })
      .eq('id', topicId)
      .select('id');
    if (error) throw error;
    return Array.isArray(data) && data.length > 0;
  }
  const { data, error } = await supabase.rpc('update_forum_topic', {
    p_topic_id: topicId,
    p_title: title,
    p_content: content
  });
  if (error) throw error;
  return data;
}

/** ──────────────────────────  Reply update  ─────────────────────────── */
export async function updateReply(
  replyId: string,
  content: string
): Promise<boolean> {
  if (!content.trim()) throw new Error('Reply content cannot be empty');

  const {
    data: { user },
    error: sessionError
  } = await supabase.auth.getUser();
  if (sessionError) throw sessionError;
  if (!user) throw new Error('Not authenticated');

  const { data: userRow, error: userError } = await supabase
    .from('users')
    .select('id')
    .eq('auth_id', user.id)
    .single();
  if (userError) throw userError;

  const { data: clubReply } = await supabase
    .from('club_forum_replies')
    .select('id')
    .eq('id', replyId)
    .maybeSingle();

  if (clubReply) {
    const { data, error } = await supabase
      .from('club_forum_replies')
      .update({ content, is_edited: true, updated_at: new Date().toISOString() })
      .eq('id', replyId)
      .eq('author_id', userRow.id)
      .select('id');
    if (error) throw error;
    return Array.isArray(data) && data.length > 0;
  }

  const { data, error } = await supabase
    .from('forum_replies')
    .update({ content, is_edited: true, updated_at: new Date().toISOString() })
    .eq('id', replyId)
    .eq('author_id', userRow.id)
    .select('id');
  if (error) throw error;
  return Array.isArray(data) && data.length > 0;
}

/* ───────── avatars for people who liked a reply ───────── */
export async function getForumReplyLikeAvatars(replyId: string) {
  const { data, error } = await supabase
    .rpc('get_forum_reply_like_avatars', { reply_id_param: replyId });
  if (error) throw error;
  return data || [];
}

export async function getClubReplyLikeAvatars(replyId: string) {
  const { data, error } = await supabase
    .rpc('get_club_reply_like_avatars', { reply_id_param: replyId });
  if (error) throw error;
  return data || [];
}



/**
 * Convenience wrapper — call the right RPC automatically.
 * Pass `isClub` = true when the reply belongs to a club topic.
 */
export async function getReplyLikeAvatars(
  replyId: string,
  isClub: boolean
) {
  return isClub
    ? getClubReplyLikeAvatars(replyId)
    : getForumReplyLikeAvatars(replyId);
}


/** ─────────────────────────  Topic delete  ──────────────────────────── */
export async function deleteTopic(topicId: string): Promise<boolean> {
  const { data: isClubTopic } = await supabase.rpc('club_forum_topic_exists', {
    topic_id_param: topicId
  });

  if (isClubTopic) {
    // 1️⃣ Delete all replies for that topic first
    const { error: delRepliesErr } = await supabase
      .from('club_forum_replies')
      .delete()
      .eq('topic_id', topicId);
    if (delRepliesErr) throw delRepliesErr;

    // 2️⃣ Now delete the topic itself
    const { error: delTopicErr } = await supabase
      .from('club_forum_topics')
      .delete()
      .eq('id', topicId);
    if (delTopicErr) throw delTopicErr;

    return true;
  }

  // Fallback to global forum delete
  const { data, error } = await supabase.rpc('delete_forum_topic', {
    p_topic_id: topicId
  });
  if (error) throw error;
  return data;
}


/** ─────────────────────────  Reply delete  ──────────────────────────── */
export async function deleteReply(replyId: string): Promise<boolean> {
  // first, see if it's a club reply:
  const { data: clubReply } = await supabase
    .from('club_forum_replies')
    .select('id')
    .eq('id', replyId)
    .maybeSingle();

  if (clubReply) {
    // delete directly
    const { error } = await supabase
      .from('club_forum_replies')
      .delete()
      .eq('id', replyId);
    if (error) throw error;
    return true;
  }

  // otherwise fall back to the RPC, but swallow "not found"
  try {
    const { data, error } = await supabase
      .rpc('delete_forum_reply', { p_reply_id: replyId });

    if (error) {
      // if the "reply not found" case, consider that a success
      if (error.code === 'P0001' && error.message.includes('Reply not found')) {
        return true;
      }
      throw error;
    }

    return data;
  } catch (e: any) {
    // Supabase.js may throw on HTTP 400 before returning { error }
    if (
      e.code === 'P0001' ||
      String(e.message).includes('Reply not found')
    ) {
      return true;
    }
    throw e;
  }
}


/* ─────────────────────  Category + Topic fetch helpers  ────────────────── */

export async function getForumCategories(): Promise<ForumCategory[]> {
  const { data, error } = await supabase
    .from('forum_categories')
    .select('*')
    .order('order', { ascending: true });
  if (error) throw error;
  return data.map(cat => ({
    id: cat.id,
    name: cat.name,
    description: cat.description,
    slug: cat.slug,
    icon: cat.icon,
    order: cat.order,
    topicsCount: cat.topics_count,
    postsCount: cat.posts_count,
    lastActivity: cat.last_activity
  }));
}

export async function getForumCategoryBySlug(
  slug: string
): Promise<ForumCategory | null> {
  const { data, error } = await supabase
    .from('forum_categories')
    .select('*')
    .eq('slug', slug)
    .single();
  if (error?.code === 'PGRST116') return null;
  if (error) throw error;
  return {
    id: data.id,
    name: data.name,
    description: data.description,
    slug: data.slug,
    icon: data.icon,
    order: data.order,
    topicsCount: data.topics_count,
    postsCount: data.posts_count,
    lastActivity: data.last_activity
  };
}

export async function getForumCategory(
  categoryId: string
): Promise<ForumCategory | null> {
  const { data, error } = await supabase
    .from('forum_categories')
    .select('*')
    .eq('id', categoryId)
    .single();
  if (error?.code === 'PGRST116') return null;
  if (error) throw error;
  return {
    id: data.id,
    name: data.name,
    description: data.description,
    slug: data.slug,
    icon: data.icon,
    order: data.order,
    topicsCount: data.topics_count,
    postsCount: data.posts_count,
    lastActivity: data.last_activity
  };
}

/** ──────────── Fetch all categories for a given club ──────────── */
export async function getClubForumCategories(
  clubId: string,
  isMember: boolean,
  isAdmin: boolean
): Promise<
  {
    id: string;
    name: string;
    slug: string;
    ordering: number;
    topics_count: number;
    posts_count: number;
    emoji: string;
    visibility: 'public' | 'members' | 'admin';
    description?: string;
  }[]
> {
  let query = supabase
    .from('club_forum_categories')
    .select(
      'id, name, slug, ordering, topics_count, posts_count, emoji, visibility, description'
    )
    .eq('club_id', clubId)
    .order('ordering', { ascending: true });

  // only filter if user is NOT an admin
  if (!isAdmin) {
    if (isMember) {
      // members see both public and members categories
      query = query.in('visibility', ['public', 'members']);
    } else {
      // non-members see only public categories
      query = query.eq('visibility', 'public');
    }
  }

  const { data, error } = await query;
  if (error) throw error;
  return data || [];
}


export async function getClubForumCategoryBySlug(
  slug: string,
  clubId: string,
  isMember: boolean,
  isAdmin: boolean
): Promise<ForumCategory | null> {
  let query = supabase
    .from('club_forum_categories')
    .select('*')
    .eq('slug', slug)
    .eq('club_id', clubId)
    .limit(1)
    .single();

  if (!isAdmin) {
    if (isMember) {
      query = query.in('visibility', ['public', 'members']);
    } else {
      query = query.eq('visibility', 'public');
    }
  }

  const { data, error } = await query;
  if (error?.code === 'PGRST116') return null;
  if (error) throw error;

  return data;
}




/* ──────────────────────  Topics & replies fetch  ──────────────────────── */
export async function getCategoryTopics(
  categoryId: string
): Promise<ForumTopic[]> {
  const { data, error } = await supabase
    .from('forum_topics')
    .select(
      `*,
       author:author_id(id, auth_id, full_name, avatar_url, city),
       last_reply_author:last_reply_author_id(id, auth_id, full_name, avatar_url)`
    )
    .eq('category_id', categoryId)
    .order('is_pinned', { ascending: false })
    .order('last_reply_at', { ascending: false })
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data.map(t => ({
    id: t.id,
    title: t.title,
    slug: t.slug,
    content: t.content,
    categoryId: t.category_id,
    authorId: t.author_id,
    author: {
      id: t.author.auth_id,
      name: t.author.full_name,
      image:
        t.author.avatar_url ||
        'https://hebbkx1anhila5yf.public.blob.vercel-storage.com/gold_symbol-removebg-n5agUlyNamyXXN0FU8QGzScjug8DVS.png',
      city: t.author.city
    },
    createdAt: t.created_at,
    updatedAt: t.updated_at,
    viewsCount: t.views_count,
    repliesCount: t.replies_count,
    isPinned: t.is_pinned,
    isLocked: t.is_locked,
    lastReplyAt: t.last_reply_at,
    lastReplyAuthorId: t.last_reply_author_id,
    lastReplyAuthor: t.last_reply_author
      ? {
          id: t.last_reply_author.auth_id,
          name: t.last_reply_author.full_name,
          image:
            t.last_reply_author.avatar_url ||
            'https://hebbkx1anhila5yf.public.blob.vercel-storage.com/gold_symbol-removebg-n5agUlyNamyXXN0FU8QGzScjug8DVS.png'
        }
      : undefined
  }));
}

/* ───────────────────────────  Full topic fetch  ────────────────────────── */
export async function getTopic(topicId: string): Promise<ForumTopic | null> {
  await supabase.rpc('increment_counter', {
    row_id: topicId,
    table_name: 'forum_topics',
    counter_name: 'views_count'
  });

  const { data: isClubTopic } = await supabase.rpc('club_forum_topic_exists', {
    topic_id_param: topicId
  });
  if (isClubTopic) {
    const { data, error } = await supabase.rpc('get_club_forum_topic', {
      topic_id_param: topicId
    });
    if (error || !data?.length) return null;
    const d = data[0];
    return {
  id: d.id,
  title: d.title,
  slug: '',
  content: d.content,
  categoryId: 'club',
  authorId: d.author_id,
  author: {
    id: d.author_auth_id,
    name: d.author_full_name,
    image: d.author_avatar_url || '',
    city: d.author_city
  },
  createdAt: d.created_at,
  updatedAt: d.updated_at,
  viewsCount: d.views_count,
  repliesCount: d.replies_count,
  isPinned: d.is_pinned,
  isLocked: d.is_locked,
  lastReplyAt: d.last_reply_at,
  lastReplyAuthorId: d.last_reply_author_id,
  lastReplyAuthor: d.last_reply_author_full_name
    ? {
        id: d.last_reply_author_auth_id,
        name: d.last_reply_author_full_name,
        image: d.last_reply_author_avatar_url || ''
      }
    : undefined,
  media: d.media_url || null,
  video: d.video_url || null
};

  }

  const { data, error } = await supabase
    .from('forum_topics')
    .select(
      `*,
       author:author_id(id, auth_id, full_name, avatar_url, city),
       last_reply_author:last_reply_author_id(id, auth_id, full_name, avatar_url)`
    )
    .eq('id', topicId)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  return {
    id: data.id,
    title: data.title,
    slug: data.slug,
    content: data.content,
    categoryId: data.category_id,
    authorId: data.author_id,
    author: {
      id: data.author.auth_id,
      name: data.author.full_name,
      image: data.author.avatar_url || '',
      city: data.author.city
    },
    createdAt: data.created_at,
    updatedAt: data.updated_at,
    viewsCount: data.views_count,
    repliesCount: data.replies_count,
    isPinned: data.is_pinned,
    isLocked: data.is_locked,
    lastReplyAt: data.last_reply_at,
    lastReplyAuthorId: data.last_reply_author_id,
    lastReplyAuthor: data.last_reply_author
      ? {
          id: data.last_reply_author.auth_id,
          name: data.last_reply_author.full_name,
          image: data.last_reply_author.avatar_url || ''
        }
      : undefined
  };
}

/* ───────────────────────  Replies fetch  ─────────────────────────────── */
export async function getTopicReplies(topicId: string): Promise<ForumReply[]> {
  const {
    data: { user }
  } = await supabase.auth.getUser();

  const { data: userRow } = await supabase
    .from('users')
    .select('id')
    .eq('auth_id', user?.id)
    .single();

  const { data: isClubTopic } = await supabase.rpc('club_forum_topic_exists', {
    topic_id_param: topicId
  });
 if (isClubTopic) {
  const { data, error } = await supabase.rpc('get_club_forum_replies', {
    topic_id_param: topicId
  });
  if (error) throw error;

  const { data: likes } = await supabase
    .from('club_forum_reply_likes')
    .select('reply_id')
    .eq('user_id', userRow?.id);

  const likedIds = new Set(likes?.map(l => l.reply_id) || []);
return (data || []).map(r => ({
  id: r.id,
  content: r.content,
  topicId: r.topic_id,
  authorId: r.author_id,
  author: {
    id: r.author_auth_id,
    name: r.author_full_name,
    image: r.author_avatar_url || '',
    city: r.author_city
  },
  createdAt: r.created_at,
  updatedAt: r.updated_at,
  isEdited: r.is_edited,
  isDeleted: false,
  likes: r.likes,
  isLiked: likedIds.has(r.id),
  media: r.media_url || null,
  video: r.video_url || null,
  isClub: true                    // <── NEW
}));


}


 const { data, error } = await supabase
  .from('forum_replies')
  .select(`
    id,
    content,
    topic_id,
    author_id,
    created_at,
    updated_at,
    is_edited,
    is_deleted,
    likes,
    media_url,
    video_url,
    author:author_id(id, auth_id, full_name, avatar_url, city)
  `)

    .eq('topic_id', topicId)
    .order('created_at', { ascending: true });
  if (error) throw error;

  const { data: likes } = await supabase
    .from('forum_reply_likes')
    .select('reply_id')
    .eq('user_id', userRow?.id);

  const likedIds = new Set(likes?.map(l => l.reply_id) || []);
  return data.map(r => ({
  id: r.id,
  content: r.content,
  topicId: r.topic_id,
  authorId: r.author_id,
  author: {
    id: r.author.auth_id,
    name: r.author.full_name,
    image: r.author.avatar_url || '',
    city: r.author.city
  },
  createdAt: r.created_at,
  updatedAt: r.updated_at,
  isEdited: r.is_edited,
  isDeleted: r.is_deleted,
  likes: r.likes,
  isLiked: likedIds.has(r.id),
  media: r.media_url,
  video: r.video_url,
  isClub: false                   // <── NEW
}));


}

/** ───────────────────────  Topic creation  ─────────────────────────────── */
export async function createTopic(
  title: string,
  content: string,
  categoryId: string,
  clubId?: string,
  media?: File
): Promise<ForumTopic | null> {
  const {
    data: { user }
  } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data: authorRow } = await supabase
    .from('users')
    .select('id, full_name, avatar_url, city')
    .eq('auth_id', user.id)
    .single();

  const slug = title
    .toLowerCase()
    .replace(/[^\w\s]/g, '')
    .replace(/\s+/g, '-')
    .substring(0, 60);

  // Handle media upload if provided
  let mediaUrl = null;
  let videoUrl = null;
  
  if (media) {
    try {
      const isVideo = media.type.startsWith('video/');
      const bucket = isVideo ? 'videos' : 'social-feed';
      
      // Generate unique filename
      const timestamp = Date.now();
      const extension = media.name.split('.').pop()?.toLowerCase() || (isVideo ? 'mp4' : 'jpg');
      const sanitizedName = media.name.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
      const filePath = `forum_media/${clubId || 'global'}/${timestamp}-${sanitizedName}.${extension}`;
      
      const { url: uploadedUrl } = await storage.uploadFile({
  bucket,
  file: media
});

if (isVideo) {
  videoUrl = uploadedUrl;
} else {
  mediaUrl = uploadedUrl;
}

    } catch (error) {
      console.error('Error uploading media:', error);
      throw new Error('Failed to upload media');
    }
  }

  const urlParams = new URLSearchParams(window.location.search);
  const clubParam = urlParams.get('club');
  if (clubParam) {
    const { data } = await supabase
      .from('club_forum_topics')
      .insert({
        title,
        content,
        club_id: clubParam,
        category_id: categoryId,
        author_id: authorRow.id,
        media_url: mediaUrl,
        video_url: videoUrl
      })
      .select()
      .single();

    return {
      id: data.id,
      title: data.title,
      slug,
      content: data.content,
      categoryId: data.category_id,
      authorId: data.author_id,
      author: {
        id: user.id,
        name: authorRow.full_name,
        image: authorRow.avatar_url || '',
        city: authorRow.city
      },
      createdAt: data.created_at,
      updatedAt: data.created_at,
      viewsCount: data.views_count,
      repliesCount: data.replies_count, 
      media: data.media_url,
      video: data.video_url,
      isPinned: data.is_pinned,
      isLocked: data.is_locked
    };
  }

  const { data } = await supabase
    .from('forum_topics')
    .insert({
      title,
      slug,
      content,
      category_id: categoryId,
      author_id: authorRow.id,
      media_url: mediaUrl,
      video_url: videoUrl
    })
    .select()
    .single();

  return {
    id: data.id,
    title: data.title,
    slug: data.slug,
    content: data.content,
    categoryId: data.category_id,
    authorId: data.author_id,
    author: {
      id: user.id,
      name: authorRow.full_name,
      image: authorRow.avatar_url || '',
      city: authorRow.city
    },
    createdAt: data.created_at,
    updatedAt: data.updated_at,
    viewsCount: data.views_count,
    repliesCount: data.replies_count,
    media: data.media_url,
    video: data.video_url,
    isPinned: data.is_pinned,
    isLocked: data.is_locked
  };
}

/** ──────────────────────────  Reply creation  ─────────────────────────── */
export async function createReply(
  content: string,
  topicId: string,
  media?: File
): Promise<ForumReply | null> {
  // 1) Figure out if this is a club topic:
  const { data: isClubTopic } = await supabase
    .rpc('club_forum_topic_exists', { topic_id_param: topicId });

  // 2) Get the current user & their PK
  const {
    data: { user },
    error: sessionError
  } = await supabase.auth.getUser();
  if (sessionError) throw sessionError;
  if (!user)        throw new Error('Not authenticated');

  const { data: authorRow, error: userError } = await supabase
    .from('users')
    .select('id, full_name, avatar_url, city')
    .eq('auth_id', user.id)
    .single();
  if (userError || !authorRow) throw userError || new Error('User not found');

 // Handle media upload if provided
let mediaUrl = null;
let videoUrl = null;

if (media) {
  try {
    const isVideo = media.type.startsWith('video/');
    const bucket = isVideo ? 'videos' : 'social-feed';

    const timestamp = Date.now();
    const extension = media.name.split('.').pop()?.toLowerCase() || (isVideo ? 'mp4' : 'jpg');
    const sanitizedName = media.name.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
    const filePath = `user_directory/${user.id}/${timestamp}-${sanitizedName}.${extension}`;

    const { data: uploadData, error: uploadError } = await supabase.storage
      .from(bucket)
      .upload(filePath, media, {
        cacheControl: '3600',
        upsert: false
      });

    if (uploadError) throw uploadError;

    const { data: { publicUrl } } = supabase.storage
      .from(bucket)
      .getPublicUrl(uploadData.path);

    if (isVideo) {
      videoUrl = publicUrl;
    } else {
      mediaUrl = publicUrl;
    }

  } catch (error) {
    console.error('Error uploading media:', error);
    throw new Error('Failed to upload media');
  }
}

  // ────────────────────────────────────────────────────────
  //  IF THIS IS A CLUB FORUM TOPIC, CALL OUR SEC‐DEFINER RPC
  // ────────────────────────────────────────────────────────
  if (isClubTopic) {
    const { data, error } = await supabase
      .rpc('insert_club_forum_reply', {
        p_topic_id: topicId,
        p_content:  content,
        p_media_url: mediaUrl,
        p_video_url: videoUrl
      });

    if (error) throw error;
    if (!data || !data.length) throw new Error('Reply creation failed');

    const row = data[0];

    // update last‐reply metadata
    const { error: updErr } = await supabase
      .from('club_forum_topics')
      .update({
        last_reply_at:        new Date().toISOString(),
        last_reply_author_id: row.author_id
      })
      .eq('id', topicId);
    if (updErr) throw updErr;

    return {
      id:        row.id,
      content:   row.content,
      topicId:   row.topic_id,
      authorId:  row.author_id,
      author: {
        id:    user.id,
        name:  authorRow.full_name,
        image: authorRow.avatar_url || '',
        city:  authorRow.city
      },
      createdAt: row.created_at,
      updatedAt: row.updated_at,
      isEdited:  row.is_edited,
      isDeleted: false,
      likes:     row.likes,
      isLiked:   false,
      media:     row.media_url,
      video:     row.video_url
    };
  }


  // ────────────────────────────────────────────────────────
  //  OTHERWISE FALL BACK TO GLOBAL (NON-CLUB) REPLIES
  // ────────────────────────────────────────────────────────
  const { data, error } = await supabase
    .from('forum_replies')
    .insert({ 
      content, 
      topic_id: topicId, 
      author_id: authorRow.id,
      media_url: mediaUrl,
      video_url: videoUrl
    })
    .select()
    .single();
  if (error) throw error;
  if (!data) throw new Error('Reply creation failed');

  const { error: updTopicErr } = await supabase
    .from('forum_topics')
    .update({
      last_reply_at:        new Date().toISOString(),
      last_reply_author_id: authorRow.id
    })
    .eq('id', topicId);
  if (updTopicErr) throw updTopicErr;

  return {
    id:        data.id,
    content:   data.content,
    topicId:   data.topic_id,
    authorId:  data.author_id,
    author: {
      id:    user.id,
      name:  authorRow.full_name,
      image: authorRow.avatar_url || '',
      city:  authorRow.city
    },
    createdAt: data.created_at,
    updatedAt: data.updated_at,
    isEdited:  data.is_edited,
    isDeleted: data.is_deleted,
    likes:     data.likes,
    isLiked:   false,
    media:     data.media_url,
    video:     data.video_url
  };
}


// ──────────────────────────  Like / unlike reply  ─────────────────────────
export async function toggleReplyLike(replyId: string): Promise<boolean> {
  // 1) Get current user
  const { data: { user }, error: authErr } = await supabase.auth.getUser();
  console.log('[toggleReplyLike] Auth user:', user);
  if (!user) throw new Error('Not authenticated');

  // 2) Lookup internal user ID
  const { data: userRow, error: userErr } = await supabase
    .from('users')
    .select('id, auth_id')
    .eq('auth_id', user.id)
    .single();

  console.log('[toggleReplyLike] Internal userRow:', userRow);
  if (userErr || !userRow) throw new Error('User row not found');

  // 3) Check if this is a club forum reply
  const { data: clubReply } = await supabase
    .from('club_forum_replies')
    .select('id')
    .eq('id', replyId)
    .maybeSingle();

  if (clubReply) {
    // Club path
    const { data: existing, error: checkErr } = await supabase
      .from('club_forum_reply_likes')
      .select('id')
      .eq('reply_id', replyId)
      .eq('user_id', userRow.id)
      .maybeSingle();

    console.log('[toggleReplyLike] Existing like found:', existing);

    if (existing) {
      const { error: deleteErr } = await supabase
        .from('club_forum_reply_likes')
        .delete()
        .eq('id', existing.id);
      if (deleteErr) throw deleteErr;
      return false;
    } else {
      const { error: insertErr } = await supabase
        .from('club_forum_reply_likes')
        .insert({ reply_id: replyId, user_id: userRow.id });
      if (insertErr) throw insertErr;
      return true;
    }
  }



  
  // 4) Fallback: general forum
  const { data: existing } = await supabase
    .from('forum_reply_likes')
    .select('id')
    .eq('reply_id', replyId)
    .eq('user_id', userRow.id)
    .maybeSingle();

  if (existing) {
    await supabase
      .from('forum_reply_likes')
      .delete()
      .eq('id', existing.id);
    return false;
  } else {
    await supabase
      .from('forum_reply_likes')
      .insert({ reply_id: replyId, user_id: userRow.id });
    return true;
  }
}


export async function toggleReplyLikeAndGetCount(replyId: string): Promise<{ isLiked: boolean; count: number }> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data: userRow, error: userErr } = await supabase
    .from('users')
    .select('id')
    .eq('auth_id', user.id)
    .single();
  if (userErr || !userRow) throw new Error('User not found');

  const { data, error } = await supabase.rpc('toggle_reply_like_and_get_count', {
    reply_id_param: replyId,
    user_id_param: userRow.id,
  }).single();

  if (error) throw error;

  return {
    isLiked: data.liked,
    count: data.count,
  };
}


/** ─────────────────────────  Get topic like count  ───────────────────────── */
export async function getTopicLikeCount(topicId: string): Promise<number> {
  const { data: isClub } = await supabase.rpc(
    'club_forum_topic_exists',
    { topic_id_param: topicId }
  );

  const table = isClub ? 'club_forum_topic_likes' : 'forum_topic_likes';

  const { count, error } = await supabase
    .from(table)
    .select('id', { head: true, count: 'exact' })
    .eq('topic_id', topicId);

  if (error) throw error;
  return count ?? 0;
}


/** ────────────  Avatars of people who liked a topic  ──────────── */
export async function getTopicLikeAvatars(
  topicId: string
): Promise<{ auth_id: string; full_name: string; avatar_url: string | null }[]> {
  /* 1 – club or general? */
  const { data: isClub } = await supabase.rpc(
    'club_forum_topic_exists',
    { topic_id_param: topicId }
  );

  if (isClub) {
    /* club topics already have an RPC */
    const { data, error } = await supabase.rpc(
      'get_topic_like_avatars',
      { topic_id_param: topicId }
    );
    if (error) throw error;
    return data ?? [];
  }

  /* 2 – general forum: two-step lookup */
  /* 2-a) get the last 12 like rows (newest first) */
  const { data: likeRows, error: likeErr } = await supabase
    .from('forum_topic_likes')
    .select('user_id')
    .eq('topic_id', topicId)
    .order('created_at', { ascending: false })
    .limit(12);

  if (likeErr) throw likeErr;
  if (!likeRows || likeRows.length === 0) return [];

  const userIds = likeRows.map(r => r.user_id);

  /* 2-b) fetch those users */
  const { data: users, error: usrErr } = await supabase
    .from('users')
    .select('auth_id, full_name, avatar_url')
    .in('id', userIds);

  if (usrErr) throw usrErr;

  /* 3 – return in the same order as likeRows */
  const userMap = Object.fromEntries(
    (users ?? []).map(u => [u.auth_id, u])
  );

  return likeRows
    .map(r => userMap[r.user_id])
    .filter(Boolean);
}

/** ────────────  Check if the current user liked a topic  ──────────── */
export async function getTopicLikeStatus(
  topicId: string,
  userAuthId?: string
): Promise<boolean> {
  /* 1 – who’s asking? */
  if (!userAuthId) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;
    userAuthId = user.id;
  }

  /* 2 – internal users.id */
  const { data: me } = await supabase
    .from('users')
    .select('id')
    .eq('auth_id', userAuthId)
    .maybeSingle();
  if (!me) return false;

  /* 3 – which likes table? */
  const { data: isClub } = await supabase.rpc(
    'club_forum_topic_exists',
    { topic_id_param: topicId }
  );
  const table = isClub ? 'club_forum_topic_likes' : 'forum_topic_likes';

  /* 4 – row existence check */
  const { data, error } = await supabase
    .from(table)
    .select('id')
    .eq('topic_id', topicId)
    .eq('user_id', me.id)
    .maybeSingle();

  if (error) throw error;
  return !!data;
}

/** ─────────────────────────  Toggle like with count return  ───────────────────────── */
export async function toggleTopicLike(
  topicId: string,
  userId?: string
): Promise<{ liked: boolean; count: number }> {
  console.log('[toggleTopicLike] called for', topicId);

  /* 1. get auth + internal user id */
  if (!userId) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');
    userId = user.id;
  }

  const { data: me, error: meErr } = await supabase
    .from('users')
    .select('id')
    .eq('auth_id', userId)
    .single();
  if (meErr || !me) throw meErr ?? new Error('User row not found');

  /* 2. is this a club topic? → choose table */
  const { data: isClub } = await supabase
    .rpc('club_forum_topic_exists', { topic_id_param: topicId });
  console.log('[toggleTopicLike] isClub =', isClub);

  const table = isClub ? 'club_forum_topic_likes' : 'forum_topic_likes';

  /* 3. current like? */
  const { data: existing } = await supabase
    .from(table)
    .select('id')
    .eq('topic_id', topicId)
    .eq('user_id', me.id)
    .maybeSingle();

  let liked: boolean;

  if (existing) {
    /* UN-like */
    await supabase.from(table).delete().eq('id', existing.id);
    liked = false;
  } else {
    /* LIKE – tolerate duplicates */
    try {
      await supabase.from(table).insert({ topic_id: topicId, user_id: me.id });
    } catch (e: any) {
      if (!(e.code === '23505' || e.status === 409)) throw e; // re-throw unexpected errors
    }
    liked = true;
  }

  /* 4. re-count */
  const { count, error: cntErr } = await supabase
    .from(table)
    .select('id', { head: true, count: 'exact' })
    .eq('topic_id', topicId);
  if (cntErr) throw cntErr;

  return { liked, count: count || 0 };
}

/* ────────────────── Get “liked by me” for many replies ────────────────── */
/**
 * Returns an array like [{ id: 'reply-id', liked: true | false }, …]
 * so the UI knows which replies the current user already liked.
 */
/* ───────── Get “liked by me” for an array of reply IDs ───────── */
export async function getReplyLikeStatusBatch(
  replyIds: string[],
  userAuthId: string
): Promise<{ id: string; liked: boolean }[]> {
  if (replyIds.length === 0) return [];

  /* 1. resolve internal users.id once */
  const { data: userRow } = await supabase
    .from('users')
    .select('id')
    .eq('auth_id', userAuthId)
    .single();
  if (!userRow) throw new Error('User row not found');

  /* 2. pull likes from BOTH tables in parallel */
  const [forumLikes, clubLikes] = await Promise.all([
    supabase
      .from('forum_reply_likes')
      .select('reply_id')
      .in('reply_id', replyIds)
      .eq('user_id', userRow.id),
    supabase
      .from('club_forum_reply_likes')
      .select('reply_id')
      .in('reply_id', replyIds)
      .eq('user_id', userRow.id),
  ]);

  /* 3. merge into a single Set for quick lookup */
  const likedSet = new Set<string>([
    ...(forumLikes.data?.map(r => r.reply_id) ?? []),
    ...(clubLikes.data?.map(r => r.reply_id) ?? []),
  ]);

  return replyIds.map(id => ({ id, liked: likedSet.has(id) }));
}


/* ───────────────────────────  Reports & stats  ────────────────────────── */
export async function reportForumContent(
  contentId: string,
  contentType: 'topic' | 'reply',
  reason: string,
  additionalInfo?: string
): Promise<boolean> {
  const {
    data: { user }
  } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data: authorRow } = await supabase
    .from('users')
    .select('id')
    .eq('auth_id', user.id)
    .single();

  const { error } = await supabase.from('forum_reports').insert({
    content_id: contentId,
    content_type: contentType,
    reporter_id: authorRow.id,
    reason,
    additional_info: additionalInfo
  });
  if (error) throw error;
  return true;
}

/** ─────────────────────────────  Forum stats  ─────────────────────────── */
export async function getForumStats(): Promise<ForumStats> {
  const { data, error } = await supabase.rpc('get_forum_stats');
  if (error) throw error;
  return {
    totalTopics: data.total_topics,
    totalReplies: data.total_replies,
    totalCategories: data.total_categories,
    activeUsers: data.active_users,
    mostActiveCategory: data.most_active_category
  };
}